import { useMe } from '../auth/MeContext';
import { useT } from '../I18nContext';
import { useBrandingText } from '../branding/useBrandingText';

/**
 * Sidebar dropdown for users with multiple client memberships.
 * Renders a native <select> for now; keeps a11y and mobile UX
 * solid without writing a custom listbox. If we ever want logos
 * in the dropdown options, swap to a custom popover.
 *
 * Switching is a server-side redirect (/switch-client/<id>); same
 * as the legacy template. Server clears + sets session state and
 * bounces back to /dashboard.
 */
export function ClientSwitcher() {
  const { me } = useMe();
  const t = useT();
  // Resolve strapline up-front (before any conditional return) so
  // the hook order stays stable. The hook safely returns '' when
  // me is null.
  const straplineForLang = useBrandingText('strapline');
  if (!me) return null;

  const available = me.available_clients;
  // Single-membership user → no switcher, just a tagline. Tagline
  // sources from the tenant's translated branding.strapline (Site
  // Vocabulary Branding section, falls back to English) when set;
  // otherwise the catalogue default. Keeps the sidebar feeling
  // on-brand for tenants who've configured it, generic-but-friendly
  // otherwise.
  if (available.length <= 1) {
    const strapline =
      straplineForLang.trim() ||
      t('client_switcher.default_strapline');
    return <div className="sidebar-brand-sub">{strapline}</div>;
  }

  const current = me.current_client?.client_id ?? '';

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value;
    if (!next || next === current) return;
    // Full-page nav so the cookie is sent on the redirect chain.
    window.location.assign(`/switch-client/${encodeURIComponent(next)}`);
  }

  return (
    <select
      className="sidebar-client-switcher"
      value={current}
      onChange={handleChange}
      title={t('client_switcher.switch_label')}
      aria-label={t('client_switcher.switch_label')}
    >
      {available.map((c) => (
        <option key={c.client_id} value={c.client_id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}
